
import Vue, { defineComponent, PropType } from 'vue';
import { TableColumn, SORTING } from '../design-system.interfaces';
import SvgIcon from './SvgIcon.vue';

const tableExpandDuration = 300;

export default defineComponent({
  name: 'BaseTable',
  components: {
    SvgIcon
  },
  props: {
    items: {
      type: Array,
      default: () => [] as number[]
    },
    columns: {
      type: Array as PropType<TableColumn[]>,
      required: true,
      default: () => [] as TableColumn[]
    },
    tbodyTrClass: {
      type: [String, Function],
      default: ''
    },
    clickableRows: {
      type: Boolean,
      default: false
    },
    hasExpandedRows: {
      type: Boolean,
      default: false
    },
    expandedIndex: {
      type: Array as PropType<number[]>,
      default: () => [] as number[]
    },
    disableExpandAnimation: {
      type: Boolean,
      default: false
    },
    alignment: {
      type: String,
      default: 'middle'
    },
    fixed: {
      type: Boolean,
      default: false
    }
  },

  data() {
    const columns = [...this.columns];
    const preSortedColumn = columns.filter(
      (column) =>
        column.isSortable && column.hasOwnProperty('initialSortDirection')
    );

    return {
      sort: {
        columnName: preSortedColumn[0]?.key || '',
        direction:
          preSortedColumn[0]?.initialSortDirection || SORTING.NOT_SORTED
      },
      selectedRows: [],
      closingRows: []
    };
  },

  computed: {
    hasContent(): boolean {
      return !!this.$slots.default || this.items.length > 0;
    },

    classes(): string {
      const classes = 'base-table';

      return classes;
    },

    cells(): TableColumn[] {
      if (!this.items.length) {
        return [];
      }

      const itemKeys = Object.keys(this.items[0]);

      if (!this.columns.length) {
        return itemKeys.map((itemKey: string) => ({
          key: itemKey
        }));
      }

      return this.columns;
    },

    generatedColumns(): string[] {
      if (this.columns.length) {
        return this.columns.map((column: TableColumn) => {
          if (column.label) {
            return column.label;
          }
          return column.key.replace(/([A-Z])/g, ' $1');
        });
      }

      if (!this.items.length) {
        return [];
      }
      return Object.keys(this.items[0]).map((column: string) =>
        column.replace(/([A-Z])/g, ' $1')
      );
    },

    expandAnimationDisabled(): boolean {
      return this.disableExpandAnimation || window?.isChromatic;
    }
  },

  created() {
    this.setExpandDuration();
  },

  methods: {
    // Set rows selection based on passed items on table creation
    getTableRowClass(row: unknown): string {
      if (this.tbodyTrClass) {
        if (typeof this.tbodyTrClass === 'function') {
          return this.tbodyTrClass(row);
        }
        return this.tbodyTrClass;
      }
    },

    getCurrentSortDirectionIcon(tableColumn: string) {
      const iconSortMap = {
        [SORTING.DESC]: 'arrow-down',
        [SORTING.ASC]: 'arrow-up',
        [SORTING.NOT_SORTED]: 'arrow-up-down'
      };
      return this.sort.columnName === tableColumn
        ? iconSortMap[this.sort.direction]
        : iconSortMap[SORTING.NOT_SORTED];
    },

    handleSortClick(columnName: string) {
      if (!columnName) {
        // set initial sort
        this.sort.columnName = columnName;
        this.sort.direction = SORTING.ASC;
      } else if (columnName !== this.sort.columnName) {
        // new sort
        this.sort.columnName = columnName;
        this.sort.direction = SORTING.ASC;
      } else if (this.sort.direction === SORTING.ASC) {
        // reverse sort
        this.sort.direction = SORTING.DESC;
      } else {
        // remove sort
        this.sort.columnName = undefined;
        this.sort.direction = SORTING.NOT_SORTED;
      }
      this.$emit('sort-data', this.sort);
    },

    isSorted(columnName: string): boolean {
      return (
        (this.sort.direction === SORTING.ASC ||
          this.sort.direction === SORTING.DESC) &&
        columnName === this.sort.columnName
      );
    },

    selectRow(selectedIndex: number, selected: boolean): void {
      Vue.set(this.selectedRows, selectedIndex, selected);
    },

    setExpandDuration() {
      document.documentElement.style.setProperty(
        '--table-expand-duration',
        `${tableExpandDuration}ms`
      );
    },

    handleEnter(element: HTMLTableRowElement) {
      if (this.expandAnimationDisabled) {
        return;
      }

      // base table inside the expanded row has a height but can't be seen, we need to get the height of that table
      const tableEl: HTMLTableElement = element.querySelector(
        '[data-name="base-table"]'
      );
      const height = tableEl.clientHeight;

      // we need to set the height we just obtained as the max height of the div containing 'tableEl'
      const tableContainer: HTMLDivElement = element.querySelector(
        '[data-name="base-table-container"]'
      );
      if (tableContainer) {
        tableContainer.style.maxHeight = `${height}px`;
      }
    },

    handleLeave(element: HTMLTableRowElement, done: () => void) {
      if (this.expandAnimationDisabled) {
        done();
        return;
      }

      const tableContainer: HTMLDivElement = element.querySelector(
        '[data-name="base-table-container"]'
      );

      if (tableContainer) {
        tableContainer.style.maxHeight = `0`;
        tableContainer.classList.add('base-table-container--closing');

        setTimeout(() => {
          done();
        }, tableExpandDuration);
      } else {
        done();
      }
    }
  }
});
